import type { EventBinding } from "../api/events";
import type { EventStore } from "./event-operation-store";
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const KEY = /^award:[a-z0-9]+:[0-9a-f]{36}$/;
export interface AwardOperation {
  operationType:
    | "award.policy.save"
    | "award.submit"
    | "award.approve"
    | "award.reject"
    | "award.approver.replace"
    | "award.publish"
    | "award.cancellation.submit";
  expectedVersion: string;
  poolId?: string;
  eventId?: string;
  evaluationId?: string;
  awardId?: string;
  proposalId?: string;
  stepId?: string;
  publicationId?: string;
  expectedEventVersion?: string;
  publicationState?: "Published" | "Cancelled";
  idempotencyKey: string;
  startedAt: number;
}
export type AwardIntent = Omit<AwardOperation, "idempotencyKey" | "startedAt">;
export function awardBindingKey(binding: EventBinding) {
  if (
    binding.audience !== "workspace" ||
    !UUID.test(binding.tenantId) ||
    !UUID.test(binding.accountId)
  )
    throw new Error("Invalid award binding");
  return `wio-veyra.award-operations.v1:${binding.tenantId}:${binding.accountId}:`;
}
export function awardBindingKeys(binding: EventBinding) {
  const original = awardBindingKey(binding);
  return [original, original.replace(".v1:", ".v2:")];
}
function prefixFor(
  binding: EventBinding,
  type: AwardOperation["operationType"],
) {
  return awardBindingKeys(binding)[
    type === "award.publish" || type === "award.cancellation.submit" ? 1 : 0
  ];
}
export function awardScope(row: AwardIntent) {
  return row.operationType === "award.policy.save"
    ? `policy:${row.poolId}`
    : row.operationType === "award.submit"
      ? `evaluation:${row.evaluationId}`
      : `award:${row.awardId}`;
}
function project(value: unknown): AwardOperation {
  const row = value as Partial<AwardOperation> | null;
  if (
    !row ||
    ![
      "award.policy.save",
      "award.submit",
      "award.approve",
      "award.reject",
      "award.approver.replace",
      "award.publish",
      "award.cancellation.submit",
    ].includes(row.operationType ?? "") ||
    !/^(0|[1-9]\d{0,17})$/.test(row.expectedVersion ?? "") ||
    !KEY.test(row.idempotencyKey ?? "") ||
    typeof row.startedAt !== "number" ||
    !Number.isFinite(row.startedAt) ||
    row.startedAt < 0
  )
    throw new Error("Invalid award recovery identity");
  const result: AwardOperation = {
    operationType: row.operationType!,
    expectedVersion: row.expectedVersion!,
    idempotencyKey: row.idempotencyKey!,
    startedAt: row.startedAt,
  };
  const fields =
    row.operationType === "award.policy.save"
      ? (["poolId"] as const)
      : row.operationType === "award.submit"
        ? (["eventId", "evaluationId", "awardId"] as const)
        : row.operationType === "award.publish"
          ? (["eventId", "awardId", "proposalId"] as const)
          : row.operationType === "award.cancellation.submit"
            ? (["eventId", "awardId", "publicationId"] as const)
            : (["eventId", "awardId", "proposalId", "stepId"] as const);
  for (const key of fields) {
    if (!UUID.test(row[key] ?? ""))
      throw new Error("Missing award recovery scope");
    result[key] = row[key];
  }
  if (
    row.operationType === "award.publish" ||
    row.operationType === "award.cancellation.submit"
  ) {
    if (!/^(0|[1-9]\d{0,17})$/.test(row.expectedEventVersion ?? ""))
      throw new Error("Missing award transition version");
    result.expectedEventVersion = row.expectedEventVersion;
    if (row.publicationId !== undefined) {
      if (!UUID.test(row.publicationId))
        throw new Error("Invalid publication identity");
      result.publicationId = row.publicationId;
    }
    if (row.operationType === "award.publish") {
      if (
        row.publicationState !== "Published" &&
        row.publicationState !== "Cancelled"
      )
        throw new Error("Missing publication outcome");
      result.publicationState = row.publicationState;
    }
  }
  return result;
}
export function restoreAwardOperations(
  store: EventStore,
  binding: EventBinding,
) {
  const prefixes = awardBindingKeys(binding),
    records: AwardOperation[] = [];
  for (let i = 0; i < store.length; i++) {
    const key = store.key(i);
    if (!key || !prefixes.some((prefix) => key.startsWith(prefix))) continue;
    const row = project(JSON.parse(store.getItem(key) ?? "null"));
    if (key !== prefixFor(binding, row.operationType) + row.idempotencyKey)
      throw new Error("Mismatched award recovery key");
    records.push(row);
  }
  if (records.length > 12) throw new Error("Award recovery capacity exceeded");
  return records.sort((a, b) => a.startedAt - b.startedAt);
}
export function persistAwardOperation(
  store: EventStore,
  binding: EventBinding,
  value: AwardOperation,
) {
  const row = project(value),
    before = restoreAwardOperations(store, binding);
  if (
    before.length >= 12 &&
    !before.some((x) => x.idempotencyKey === row.idempotencyKey)
  )
    throw new Error("Award recovery capacity exceeded");
  const key = prefixFor(binding, row.operationType) + row.idempotencyKey,
    text = JSON.stringify(row);
  store.setItem(key, text);
  if (store.getItem(key) !== text)
    throw new Error("Award recovery unavailable");
}
export function removeAwardOperation(
  store: EventStore,
  binding: EventBinding,
  key: string,
) {
  if (!KEY.test(key)) throw new Error("Invalid award key");
  for (const prefix of awardBindingKeys(binding)) {
    const storageKey = prefix + key;
    store.removeItem(storageKey);
    if (store.getItem(storageKey) !== null)
      throw new Error("Award recovery unavailable");
  }
}
export function awardCompletion(
  value: unknown,
  operation: AwardOperation,
): boolean {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>,
    field = (name: string) =>
      row[name] ?? row[name[0].toUpperCase() + name.slice(1)];
  if (
    String(field("version")) !==
      String(BigInt(operation.expectedVersion) + BigInt(1)) ||
    !UUID.test(String(field("resourceId")))
  )
    return false;
  if (operation.operationType === "award.policy.save")
    return (
      field("poolId") === operation.poolId &&
      UUID.test(String(field("revisionId")))
    );
  if (
    field("eventId") !== operation.eventId ||
    field("awardId") !== operation.awardId ||
    !UUID.test(String(field("proposalId")))
  )
    return false;
  if (
    operation.operationType === "award.submit" ||
    operation.operationType === "award.cancellation.submit"
  )
    return (
      field("state") === "Pending" &&
      field("resourceId") === field("proposalId")
    );
  if (operation.operationType === "award.publish")
    return (
      field("proposalId") === operation.proposalId &&
      field("publicationId") === field("resourceId") &&
      field("publicationId") !== operation.publicationId &&
      field("state") === operation.publicationState &&
      String(field("eventVersion")) ===
        String(BigInt(operation.expectedEventVersion!) + BigInt(1)) &&
      typeof field("publishedAt") === "string" &&
      Number.isFinite(Date.parse(String(field("publishedAt"))))
    );
  if (
    field("proposalId") !== operation.proposalId ||
    field("stepId") !== operation.stepId ||
    field("resourceId") !== operation.stepId
  )
    return false;
  return operation.operationType === "award.reject"
    ? field("state") === "Rejected"
    : operation.operationType === "award.approver.replace"
      ? field("state") === "Pending"
      : ["Pending", "Approved"].includes(String(field("state")));
}
